import { About } from './About/About.js';
import { type UserProfile_userProfile$key } from '@/__generated__/relay/UserProfile_userProfile.graphql.js';
import { Accordion } from '@/components/Accordion/Accordion.js';
import { HorizontalDivider } from '@/components/HorizontalDivider/HorizontalDivider.js';
import { Layout } from '@/components/Layout/Layout.js';
import { Parallax } from '@/components/Motion/Parallax/Parallax.js';
import { UserProfileFragment } from '@/documents/fragments/UserProfile.js';
import { useHeadlineVariant } from '@/hooks/useHeadlineVariant.js';
import { useIPFragment } from '@/hooks/useIPFragment.js';
import { useMatchMedia } from '@/hooks/useMatchMedia.js';
import { minWidthMediaQueries } from '@/stitches/media.js';
import { type HomePageProps } from '@/templates/pageTypes.js';
import { BaseLayout } from '@/templates/sydney/components/BaseLayout/BaseLayout.js';
import { CallToAction } from '@/templates/sydney/components/CallToAction/CallToAction.js';
import { Hero } from '@/templates/sydney/components/Homepage/Hero/Hero.js';
import { Marquee } from '@/templates/sydney/components/Homepage/Marquee/Marquee.js';
import { Recommendations } from '@/templates/sydney/components/Homepage/Recommendations/Recommendations.js';
import { ProjectsList } from '@/templates/sydney/components/ProjectsList/ProjectsList.js';
import { ServiceList } from '@/templates/sydney/components/ServiceList/ServiceList.js';

export const Home = ({ queryRef }: HomePageProps) => {
  const isMediumViewport = useMatchMedia(minWidthMediaQueries.bp2);
  const profile = useIPFragment<UserProfile_userProfile$key>(
    UserProfileFragment,
    queryRef,
  );
  const headlineFontFamily = useHeadlineVariant();

  if (!queryRef || !profile) return null;

  return (
    <BaseLayout queryRef={queryRef}>
      <Hero queryRef={queryRef} />
      {isMediumViewport ? (
        <Layout.FullWidth>
          <Marquee title={profile.title} />
        </Layout.FullWidth>
      ) : null}
      <Accordion.Root>
        <ProjectsList queryRef={queryRef} />
        <ServiceList
          queryRef={queryRef}
          title="Services"
        />
        {isMediumViewport ? (
          <>
            <Recommendations queryRef={queryRef} />
            <About queryRef={queryRef} />
            <HorizontalDivider css={{ marginTop: '$120' }} />
          </>
        ) : (
          <>
            <About queryRef={queryRef} />
            <Recommendations queryRef={queryRef} />
          </>
        )}
      </Accordion.Root>
      {isMediumViewport ? (
        <Parallax
          clampInitial
          offset={-100}
        >
          <CallToAction
            buttonLabel="Work With Me"
            fontFamily={headlineFontFamily}
            section="cta_profile"
            title="Let's work together"
            variant="inquiry"
          />
        </Parallax>
      ) : null}
    </BaseLayout>
  );
};
